import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { TextArea } from '@shared/components/ui/textarea';
import { useCountries } from '@shared/hooks/useCountries';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useCouriers } from '../../hooks/useCouriers';
import { useCourierServices } from '../../hooks/useCourierServices';
import { useCourierZones } from '../../hooks/useCourierZones';
import { RateItem, RateItemFormRef, ShippingData } from '../../models/rate.types';

// Schema básico - solo estructura. La validación real se hace en getData()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const shippingFormSchema = z.object({
  courier_id: z.string(),
  courier_service_id: z.string(),
  country_id: z.string(),
  courier_zone_id: z.string().optional(),
  notes: z.string().optional(),
  weight_ranges: z.array(
    z.object({
      min_weight: z.coerce.number(),
      max_weight: z.coerce.number(),
      amount: z.coerce.number(),
    }),
  ),
  // Return shipping
  return_shipping_policy: z.enum(['same_price', 'free', 'custom']).optional(),
  return_weight_ranges: z
    .array(
      z.object({
        min_weight: z.coerce.number(),
        max_weight: z.coerce.number(),
        amount: z.coerce.number(),
      }),
    )
    .optional(),
  // COD formato fijo
  cod_fixed_fee: z.string().optional(),
  cod_percentage_fee: z.string().optional(),
  // Cargos adicionales
  fuel_surcharge_percent: z.string().optional(),
  extra_weight_charge_amount_per_kg: z.string().optional(),
});

type ShippingFormData = z.infer<typeof shippingFormSchema>;

/**
 * Valida los datos del formulario y devuelve errores.
 * Validación 100% síncrona, sin depender de efectos ni estado de react-hook-form.
 */
function validateShippingData(values: ShippingFormData, hasZones: boolean): Record<string, string> {
  const errors: Record<string, string> = {};

  // Campos obligatorios
  if (!values.courier_id) errors.courier_id = 'validation.courierRequired';
  if (!values.courier_service_id) errors.courier_service_id = 'validation.serviceRequired';
  if (!values.country_id) errors.country_id = 'validation.countryRequired';

  // Zona obligatoria si hay zonas
  if (hasZones && (!values.courier_zone_id || values.courier_zone_id === 'none' || values.courier_zone_id === '')) {
    errors.courier_zone_id = 'validation.zoneRequired';
  }

  // Al menos un rango de peso
  if (!values.weight_ranges || values.weight_ranges.length === 0) {
    errors.weight_ranges = 'validation.weightRangesRequired';
  } else {
    // Validar que min < max en cada rango
    values.weight_ranges.forEach((range, index) => {
      const min = Number(range.min_weight);
      const max = Number(range.max_weight);
      if (!isNaN(min) && !isNaN(max) && min >= max) {
        errors[`weight_ranges.${index}.max_weight`] = `Rango ${index + 1}: máximo debe ser mayor que mínimo`;
      }
    });
  }

  // Return shipping policy validations
  if (values.return_shipping_policy) {
    if (values.return_shipping_policy === 'custom') {
      // Si es custom, return_weight_ranges es requerido
      if (!values.return_weight_ranges || values.return_weight_ranges.length === 0) {
        errors.return_weight_ranges = 'validation.returnWeightRangesRequired';
      } else {
        // Validar que min < max en cada rango de vuelta
        values.return_weight_ranges.forEach((range, index) => {
          const min = Number(range.min_weight);
          const max = Number(range.max_weight);
          if (!isNaN(min) && !isNaN(max) && min >= max) {
            errors[`return_weight_ranges.${index}.max_weight`] =
              `Rango vuelta ${index + 1}: máximo debe ser mayor que mínimo`;
          }
        });
      }
    } else if (values.return_shipping_policy === 'free' || values.return_shipping_policy === 'same_price') {
      // Si es free o same_price, NO debe haber return_weight_ranges
      if (values.return_weight_ranges && values.return_weight_ranges.length > 0) {
        errors.return_weight_ranges = 'validation.returnWeightRangesNotAllowed';
      }
    }
  } else {
    // Si no hay return_shipping_policy, NO debe haber return_weight_ranges
    if (values.return_weight_ranges && values.return_weight_ranges.length > 0) {
      errors.return_weight_ranges = 'validation.returnWeightRangesNotAllowed';
    }
  }

  // COD validations: ambos campos requeridos si hay alguno
  const hasCodFixed = values.cod_fixed_fee?.trim() || values.cod_percentage_fee?.trim();
  if (hasCodFixed) {
    if (!values.cod_fixed_fee?.trim()) errors.cod_fixed_fee = 'validation.codBothFieldsRequired';
    if (!values.cod_percentage_fee?.trim()) errors.cod_percentage_fee = 'validation.codBothFieldsRequired';
  }

  // Cargos adicionales: ambos o ninguno
  const hasAnyExtra = values.fuel_surcharge_percent?.trim() || values.extra_weight_charge_amount_per_kg?.trim();
  if (hasAnyExtra) {
    if (!values.fuel_surcharge_percent?.trim())
      errors.fuel_surcharge_percent = 'validation.extraChargesBothFieldsRequired';
    if (!values.extra_weight_charge_amount_per_kg?.trim())
      errors.extra_weight_charge_amount_per_kg = 'validation.extraChargesBothFieldsRequired';
  }

  return errors;
}

/**
 * Normaliza los rangos de peso: asegura que el primero empiece en 0
 * y que sean consecutivos. Sin depender de efectos.
 */
function normalizeWeightRanges(ranges: ShippingFormData['weight_ranges']): ShippingFormData['weight_ranges'] {
  if (!ranges || ranges.length === 0) return ranges;

  const round3 = (v: number) => Math.round(v * 1000) / 1000;

  return ranges.map((range, index) => {
    const min = index === 0 ? 0 : round3(Number(ranges[index - 1].max_weight) + 0.001);

    return {
      min_weight: min,
      max_weight: Number(range.max_weight),
      amount: Number(range.amount),
    };
  });
}

interface AddShippingItemFormProps {
  initialData?: RateItem;
}

const AddShippingItemFormComponent = forwardRef<RateItemFormRef, AddShippingItemFormProps>(({ initialData }, ref) => {
  const { t } = useTranslation('ratesModule');
  const currency = useCurrency();
  const prevCourierIdRef = useRef<string>(initialData?.courier_id ? String(initialData.courier_id) : '');

  const { data: couriers = [] } = useCouriers();
  const { countryOptions } = useCountries();

  // Extraer datos iniciales del item para edición
  const shippingData = initialData?.data as ShippingData | undefined;

  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ShippingFormData>({
    mode: 'onSubmit',
    defaultValues: {
      courier_id: initialData?.courier_id ? String(initialData.courier_id) : '',
      courier_service_id: initialData?.courier_service_id ? String(initialData.courier_service_id) : '',
      country_id: initialData?.country_id ? String(initialData.country_id) : '',
      courier_zone_id: initialData?.courier_zone_id ? String(initialData.courier_zone_id) : '',
      notes: initialData?.notes || '',
      weight_ranges: shippingData?.weight_ranges?.length
        ? shippingData.weight_ranges
        : [{ min_weight: 0, max_weight: 1, amount: 0 }],
      // Return shipping
      return_shipping_policy: shippingData?.return_shipping_policy || undefined,
      return_weight_ranges: shippingData?.return_weight_ranges || [],
      // COD formato fijo
      cod_fixed_fee:
        shippingData?.cash_on_delivery?.fixed_fee != null ? String(shippingData.cash_on_delivery.fixed_fee) : '',
      cod_percentage_fee:
        shippingData?.cash_on_delivery?.percentage_fee != null
          ? String(shippingData.cash_on_delivery.percentage_fee * 100) // API stores as decimal, show as %
          : '',
      // Cargos adicionales
      fuel_surcharge_percent:
        shippingData?.extra_charges?.fuel_surcharge_percent != null
          ? String(shippingData.extra_charges.fuel_surcharge_percent)
          : '',
      extra_weight_charge_amount_per_kg:
        shippingData?.extra_charges?.extra_weight_charge_amount_per_kg != null
          ? String(shippingData.extra_charges.extra_weight_charge_amount_per_kg)
          : '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'weight_ranges',
  });

  const {
    fields: returnWeightRangesFields,
    append: appendReturnRange,
    remove: removeReturnRange,
  } = useFieldArray({
    control,
    name: 'return_weight_ranges',
  });

  const selectedCourierId = useWatch({ control, name: 'courier_id' });
  const selectedCountryId = useWatch({ control, name: 'country_id' });
  const weightRanges = useWatch({ control, name: 'weight_ranges' });
  const returnShippingPolicy = useWatch({ control, name: 'return_shipping_policy' });
  const returnWeightRanges = useWatch({ control, name: 'return_weight_ranges' });

  const { data: services = [] } = useCourierServices(selectedCourierId);
  const { data: zones = [], isLoading: isLoadingZones } = useCourierZones(selectedCountryId);

  useEffect(() => {
    if (prevCourierIdRef.current && prevCourierIdRef.current !== selectedCourierId) {
      setValue('courier_service_id', '');
    }
    prevCourierIdRef.current = selectedCourierId;
  }, [selectedCourierId, setValue]);

  // Track previous country to detect changes (inicializar con el valor inicial si existe)
  const prevCountryIdRef = useRef<string>(initialData?.country_id ? String(initialData.country_id) : '');

  // Clear zone when country changes (only when country actually changes)
  useEffect(() => {
    if (selectedCountryId && prevCountryIdRef.current !== selectedCountryId) {
      setValue('courier_zone_id', '', { shouldValidate: false });
      prevCountryIdRef.current = selectedCountryId;
    } else if (!selectedCountryId) {
      prevCountryIdRef.current = '';
    }
  }, [selectedCountryId, setValue]);

  // Función para redondear a 3 decimales máximo
  const roundToThreeDecimals = useCallback((value: number): number => {
    return Math.round(value * 1000) / 1000;
  }, []);

  // Función para redondear a 2 decimales máximo (para importes y porcentajes)
  const roundToTwoDecimals = useCallback((value: number): number => {
    return Math.round(value * 100) / 100;
  }, []);

  // Efecto simplificado para sincronizar rangos - solo ajusta el primer rango
  useEffect(() => {
    if (weightRanges?.[0]?.min_weight !== 0) {
      setValue('weight_ranges.0.min_weight', 0, { shouldDirty: false });
    }
  }, [weightRanges?.[0]?.min_weight, setValue]);

  useImperativeHandle(
    ref,
    () => ({
      getData: async () => {
        // Limpiar errores anteriores
        clearErrors();

        // Obtener valores actuales del formulario
        const values = getValues();

        // Validar con función pura (sin depender de estado de react-hook-form)
        const validationErrors = validateShippingData(values, zones.length > 0);

        if (Object.keys(validationErrors).length > 0) {
          // Mostrar errores en los campos correspondientes
          Object.entries(validationErrors).forEach(([path, message]) => {
            setError(path as any, { type: 'manual', message });
          });
          return null;
        }

        // Normalizar rangos de peso (corrige min automáticamente)
        const normalizedRanges = normalizeWeightRanges(values.weight_ranges);
        if (!normalizedRanges || normalizedRanges.length === 0) return null;

        const data: Record<string, any> = {
          weight_ranges: normalizedRanges,
        };

        // Return shipping policy
        if (values.return_shipping_policy) {
          data.return_shipping_policy = values.return_shipping_policy;
          if (
            values.return_shipping_policy === 'custom' &&
            values.return_weight_ranges &&
            values.return_weight_ranges.length > 0
          ) {
            // Normalizar rangos de vuelta también
            const normalizedReturnRanges = normalizeWeightRanges(values.return_weight_ranges);
            if (normalizedReturnRanges && normalizedReturnRanges.length > 0) {
              data.return_weight_ranges = normalizedReturnRanges;
            }
          }
        }

        // cash_on_delivery: formato fijo
        const hasCodFixed = values.cod_fixed_fee?.trim() && values.cod_percentage_fee?.trim();
        if (hasCodFixed) {
          data.cash_on_delivery = {
            fixed_fee: parseFloat(values.cod_fixed_fee!),
            percentage_fee: parseFloat(values.cod_percentage_fee!) / 100, // Convert to decimal (1% = 0.01)
          };
        }

        // extra_charges: if present, BOTH fields are required
        const hasBothExtraFields = values.fuel_surcharge_percent && values.extra_weight_charge_amount_per_kg;
        if (hasBothExtraFields && values.fuel_surcharge_percent && values.extra_weight_charge_amount_per_kg) {
          data.extra_charges = {
            fuel_surcharge_percent: parseFloat(values.fuel_surcharge_percent),
            extra_weight_charge_amount_per_kg: parseFloat(values.extra_weight_charge_amount_per_kg),
          };
        }

        // Generate title automatically
        const courier = couriers.find((c) => c.id === values.courier_id);
        const service = services.find((s) => s.id === values.courier_service_id);
        const country = countryOptions.find((c) => String(c.id) === values.country_id);
        const zone =
          values.courier_zone_id && values.courier_zone_id !== 'none'
            ? zones.find((z) => z.id === Number(values.courier_zone_id))
            : null;

        const titleParts: string[] = [];
        if (courier?.name) titleParts.push(courier.name);
        if (service?.name) titleParts.push(service.name);
        if (country?.name) titleParts.push(country.name);
        if (zone?.name_es || zone?.name) {
          titleParts.push(`(${zone.name_es || zone.name})`);
        }
        const title = titleParts.join(' - ');

        return {
          title,
          type: 'shipping' as const,
          courier_id: parseInt(values.courier_id),
          courier_service_id: parseInt(values.courier_service_id),
          country_id: parseInt(values.country_id),
          courier_zone_id:
            values.courier_zone_id && values.courier_zone_id !== 'none' ? parseInt(values.courier_zone_id) : null,
          notes: values.notes || null,
          data,
          // Nombres resueltos para mostrar en la UI
          _resolvedNames: {
            courier_name: courier?.name || null,
            courier_service_name: service?.name || null,
            country_name: country?.name || null,
            courier_zone_name: zone?.name_es || zone?.name || null,
          },
        };
      },
    }),
    [getValues, setError, clearErrors, zones, couriers, services, countryOptions],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.shipping.courier')} *</Label>
          <Controller
            name="courier_id"
            control={control}
            render={({ field }) => (
              <Combobox
                options={couriers.map((c) => ({
                  label: c.name,
                  value: c.id,
                }))}
                value={field.value}
                setValue={field.onChange}
                placeholder={t('form.shipping.selectCourier')}
                buttonClassName={errors.courier_id ? 'border-error' : ''}
                dataTestId="courier-combobox"
              />
            )}
          />
          {errors.courier_id && <p className="text-xs text-error mt-1">{t(errors.courier_id.message!)}</p>}
        </div>

        <div>
          <Label>{t('form.shipping.service')} *</Label>
          <Controller
            name="courier_service_id"
            control={control}
            render={({ field }) => (
              <Combobox
                options={services.map((s) => ({
                  label: s.name,
                  value: s.id,
                }))}
                value={field.value}
                setValue={field.onChange}
                placeholder={t('form.shipping.selectService')}
                disabled={!selectedCourierId}
                buttonClassName={errors.courier_service_id ? 'border-error' : ''}
                dataTestId="service-combobox"
              />
            )}
          />
          {errors.courier_service_id && (
            <p className="text-xs text-error mt-1">{t(errors.courier_service_id.message!)}</p>
          )}
        </div>

        <div>
          <Label>{t('form.shipping.country')} *</Label>
          <Controller
            name="country_id"
            control={control}
            render={({ field }) => (
              <Combobox
                options={countryOptions.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                }))}
                value={field.value}
                setValue={field.onChange}
                placeholder={t('form.shipping.selectCountry')}
                buttonClassName={errors.country_id ? 'border-error' : ''}
                dataTestId="country-combobox"
              />
            )}
          />
          {errors.country_id && <p className="text-xs text-error mt-1">{t(errors.country_id.message!)}</p>}
        </div>

        <div>
          <Label>
            {t('form.shipping.zone')}
            {zones.length > 0 && ' *'}
          </Label>
          <Controller
            name="courier_zone_id"
            control={control}
            render={({ field }) => {
              // Determinar el valor a mostrar
              // Si hay un valor guardado y las zonas están cargando, mantener el valor
              const hasValidValue = field.value && field.value !== '' && field.value !== 'none';
              const zonesLoaded = !isLoadingZones && zones.length > 0;
              const noZonesAvailable = !isLoadingZones && zones.length === 0;

              let displayValue: string;
              if (hasValidValue && (zonesLoaded || isLoadingZones)) {
                // Mantener el valor seleccionado
                displayValue = field.value || '';
              } else if (noZonesAvailable) {
                displayValue = 'none';
              } else if (!field.value || field.value === 'none') {
                displayValue = '';
              } else {
                displayValue = field.value;
              }

              return (
                <Select
                  key={`zone-${zones.length}-${field.value}`}
                  value={displayValue}
                  onValueChange={(v) => {
                    const newValue = v === 'none' ? '' : v;
                    field.onChange(newValue);
                  }}
                  disabled={!selectedCountryId || isLoadingZones}
                >
                  <SelectTrigger className={errors.courier_zone_id ? 'border-error' : ''}>
                    <SelectValue
                      placeholder={
                        isLoadingZones
                          ? 'Cargando zonas...'
                          : zones.length === 0
                            ? t('form.shipping.noZone')
                            : t('form.shipping.selectZone')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.length === 0 && <SelectItem value="none">{t('form.shipping.noZone')}</SelectItem>}
                    {zones.map((zone) => (
                      <SelectItem key={zone.id} value={String(zone.id)}>
                        {zone.name_es || zone.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
          {errors.courier_zone_id && <p className="text-xs text-error mt-1">{t(errors.courier_zone_id.message!)}</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>{t('form.shipping.weightRanges')} *</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const currentRanges = getValues('weight_ranges');
              const lastMax =
                currentRanges.length > 0 ? Number(currentRanges[currentRanges.length - 1]?.max_weight) : 0;
              const nextMin = roundToThreeDecimals(lastMax + 0.001);
              append({
                min_weight: nextMin,
                max_weight: roundToThreeDecimals(nextMin + 1),
                amount: 0,
              });
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="w-3 h-3 mr-1" />
            {t('form.shipping.addRange')}
          </Button>
        </div>
        {errors.weight_ranges && (
          <div className="text-xs text-error mb-2 space-y-1">
            <p className="font-medium">⚠️ Errores en rangos de peso:</p>
            {Array.isArray(errors.weight_ranges) ? (
              errors.weight_ranges.map((rangeError: any, idx: number) => {
                if (!rangeError) return null;
                return Object.keys(rangeError).map((field) => {
                  const fieldError = rangeError[field];
                  if (fieldError?.message) {
                    return (
                      <p key={`${idx}-${field}`} className="pl-4">
                        Rango {idx + 1} - {field === 'min_weight' ? 'Min' : field === 'max_weight' ? 'Max' : 'Importe'}:{' '}
                        {fieldError.message}
                      </p>
                    );
                  }
                  return null;
                });
              })
            ) : (
              <p className="pl-4">
                {(errors.weight_ranges as any)?.root?.message ||
                  errors.weight_ranges.message ||
                  t('validation.weightRangesRequired')}
              </p>
            )}
          </div>
        )}
        {fields.length === 0 && (
          <div className="text-sm text-subtext py-3 text-center border border-dashed border-card-border rounded">
            {t('form.shipping.noRanges')}
          </div>
        )}
        {fields.length > 0 && (
          <div className="border border-card-border rounded overflow-hidden">
            <div className="bg-muted/30 px-3 py-1.5 border-b border-card-border">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-subtext">
                <div className="col-span-3">Min (kg)</div>
                <div className="col-span-3">Max (kg)</div>
                <div className="col-span-5">Importe ({currency})</div>
                <div className="col-span-1"></div>
              </div>
            </div>
            <div className="divide-y divide-card-border">
              {fields.map((field, index) => {
                const isFirst = index === 0;
                const rangeErrors = errors.weight_ranges?.[index];
                const minError = rangeErrors?.min_weight;
                const maxError = rangeErrors?.max_weight;
                const amountError = rangeErrors?.amount;

                return (
                  <div
                    key={field.id}
                    className={`px-3 py-1.5 hover:bg-muted/10 transition-colors ${
                      minError || maxError || amountError ? 'bg-error/5' : ''
                    }`}
                  >
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-3">
                        <Controller
                          name={`weight_ranges.${index}.min_weight`}
                          control={control}
                          render={({ field: formField }) => (
                            <div>
                              <Input
                                {...formField}
                                type="number"
                                step="0.001"
                                placeholder="0.000"
                                className={`w-full h-8 text-sm ${minError ? 'border-error' : ''}`}
                                min="0"
                                disabled={true}
                                onChange={(e) => {
                                  // Handle both comma and dot as decimal separator
                                  const normalizedValue = e.target.value.replace(',', '.');
                                  const value = parseFloat(normalizedValue);
                                  if (!isNaN(value) && value >= 0) {
                                    const rounded = roundToThreeDecimals(value);
                                    formField.onChange(rounded);
                                  } else if (normalizedValue === '' || normalizedValue === '-') {
                                    // Allow empty or minus sign while typing
                                    formField.onChange(e.target.value);
                                  }
                                }}
                                value={isFirst ? 0 : formField.value}
                              />
                              {minError && <p className="text-xs text-error mt-0.5">{minError.message}</p>}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-span-3">
                        <Controller
                          name={`weight_ranges.${index}.max_weight`}
                          control={control}
                          render={({ field: formField }) => (
                            <div>
                              <Input
                                {...formField}
                                type="number"
                                step="0.001"
                                placeholder="0.000"
                                className={`w-full h-8 text-sm ${maxError ? 'border-error' : ''}`}
                                min="0"
                                onChange={(e) => {
                                  // Handle both comma and dot as decimal separator
                                  const normalizedValue = e.target.value.replace(',', '.');
                                  const value = parseFloat(normalizedValue);
                                  if (!isNaN(value) && value >= 0) {
                                    const rounded = roundToThreeDecimals(value);
                                    formField.onChange(rounded);
                                  } else if (normalizedValue === '' || normalizedValue === '-') {
                                    // Allow empty or minus sign while typing
                                    formField.onChange(e.target.value);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Handle both comma and dot as decimal separator
                                  const normalizedValue = e.target.value.replace(',', '.');
                                  const value = parseFloat(normalizedValue);
                                  if (!isNaN(value) && value >= 0) {
                                    const rounded = roundToThreeDecimals(value);
                                    const minValue = Number(weightRanges?.[index]?.min_weight ?? 0);
                                    // Validar que el máximo sea mayor que el mínimo al salir del campo
                                    if (rounded <= minValue) {
                                      // Si el valor es menor o igual al mínimo, establecerlo como mínimo + 0.001
                                      const validMax = roundToThreeDecimals(minValue + 0.001);
                                      formField.onChange(validMax);
                                      // Actualizar el siguiente rango automáticamente
                                      if (index < fields.length - 1) {
                                        const nextMinValue = roundToThreeDecimals(validMax + 0.001);
                                        setValue(`weight_ranges.${index + 1}.min_weight`, nextMinValue);
                                      }
                                    } else {
                                      // Actualizar el siguiente rango automáticamente
                                      if (index < fields.length - 1) {
                                        const nextMinValue = roundToThreeDecimals(rounded + 0.001);
                                        setValue(`weight_ranges.${index + 1}.min_weight`, nextMinValue);
                                      }
                                    }
                                  }
                                  formField.onBlur();
                                }}
                              />
                              {maxError && <p className="text-xs text-error mt-0.5">{maxError.message}</p>}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-span-5">
                        <Controller
                          name={`weight_ranges.${index}.amount`}
                          control={control}
                          render={({ field: formField }) => (
                            <div>
                              <Input
                                {...formField}
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className={`w-full h-8 text-sm ${amountError ? 'border-error' : ''}`}
                                min="0"
                                onChange={(e) => {
                                  // Handle both comma and dot as decimal separator
                                  const normalizedValue = e.target.value.replace(',', '.');
                                  const value = parseFloat(normalizedValue);
                                  if (!isNaN(value) && value >= 0) {
                                    const rounded = roundToTwoDecimals(value);
                                    formField.onChange(rounded);
                                  } else if (normalizedValue === '' || normalizedValue === '-') {
                                    // Allow empty or minus sign while typing
                                    formField.onChange(e.target.value);
                                  }
                                }}
                              />
                              {amountError && <p className="text-xs text-error mt-0.5">{amountError.message}</p>}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // Solo ajustar el siguiente rango si NO estamos eliminando el último
                              const isRemovingLast = index === fields.length - 1;
                              remove(index);
                              if (!isRemovingLast) {
                                setTimeout(() => {
                                  if (index === 0) {
                                    setValue('weight_ranges.0.min_weight', 0);
                                    return;
                                  }

                                  const prevMax = getValues(`weight_ranges.${index - 1}.max_weight`);
                                  if (prevMax !== undefined) {
                                    const nextMinValue = roundToThreeDecimals(Number(prevMax) + 0.001);
                                    setValue(`weight_ranges.${index}.min_weight`, nextMinValue);
                                  }
                                }, 0);
                              }
                            }}
                            className="h-8 w-8 p-0 text-error hover:text-error hover:bg-error/10"
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Return Shipping Policy */}
      <div className="border-t border-card-border pt-4">
        <Label className="mb-2">{t('form.shipping.returnShippingPolicy')}</Label>
        <Controller
          name="return_shipping_policy"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || 'same_price'}
              onValueChange={(v) => {
                field.onChange(v === 'same_price' ? undefined : v);
                // Limpiar return_weight_ranges si no es custom
                if (v !== 'custom') {
                  setValue('return_weight_ranges', []);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('form.shipping.selectReturnPolicy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="same_price">{t('form.shipping.returnPolicySamePrice')}</SelectItem>
                <SelectItem value="free">{t('form.shipping.returnPolicyFree')}</SelectItem>
                <SelectItem value="custom">{t('form.shipping.returnPolicyCustom')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.return_shipping_policy && (
          <p className="text-xs text-error mt-1">{t(errors.return_shipping_policy.message!)}</p>
        )}
      </div>

      {/* Return Weight Ranges (solo si es custom) */}
      {returnShippingPolicy === 'custom' && (
        <div className="border-t border-card-border pt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>{t('form.shipping.returnWeightRanges')} *</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const currentRanges = getValues('return_weight_ranges') || [];
                const lastMax =
                  currentRanges.length > 0 ? Number(currentRanges[currentRanges.length - 1]?.max_weight) : 0;
                const nextMin = roundToThreeDecimals(lastMax + 0.001);
                appendReturnRange({
                  min_weight: nextMin,
                  max_weight: roundToThreeDecimals(nextMin + 1),
                  amount: 0,
                });
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="w-3 h-3 mr-1" />
              {t('form.shipping.addRange')}
            </Button>
          </div>
          {errors.return_weight_ranges && (
            <div className="text-xs text-error mb-2 space-y-1">
              <p className="font-medium">⚠️ Errores en rangos de peso de vuelta:</p>
              {Array.isArray(errors.return_weight_ranges) ? (
                errors.return_weight_ranges.map((rangeError: any, idx: number) => {
                  if (!rangeError) return null;
                  return Object.keys(rangeError).map((field) => {
                    const fieldError = rangeError[field];
                    if (fieldError?.message) {
                      return (
                        <p key={`${idx}-${field}`} className="pl-4">
                          Rango vuelta {idx + 1} -{' '}
                          {field === 'min_weight' ? 'Min' : field === 'max_weight' ? 'Max' : 'Importe'}:{' '}
                          {fieldError.message}
                        </p>
                      );
                    }
                    return null;
                  });
                })
              ) : (
                <p className="pl-4">
                  {(errors.return_weight_ranges as any)?.root?.message ||
                    errors.return_weight_ranges.message ||
                    t('validation.returnWeightRangesRequired')}
                </p>
              )}
            </div>
          )}
          {returnWeightRangesFields.length === 0 && (
            <div className="text-sm text-subtext py-3 text-center border border-dashed border-card-border rounded">
              {t('form.shipping.noRanges')}
            </div>
          )}
          {returnWeightRangesFields.length > 0 && (
            <div className="border border-card-border rounded overflow-hidden">
              <div className="bg-muted/30 px-3 py-1.5 border-b border-card-border">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-subtext">
                  <div className="col-span-3">Min (kg)</div>
                  <div className="col-span-3">Max (kg)</div>
                  <div className="col-span-5">Importe ({currency})</div>
                  <div className="col-span-1"></div>
                </div>
              </div>
              <div className="divide-y divide-card-border">
                {returnWeightRangesFields.map((field, index) => {
                  const isFirst = index === 0;
                  const rangeErrors = errors.return_weight_ranges?.[index];
                  const minError = rangeErrors?.min_weight;
                  const maxError = rangeErrors?.max_weight;
                  const amountError = rangeErrors?.amount;

                  return (
                    <div
                      key={field.id}
                      className={`px-3 py-1.5 hover:bg-muted/10 transition-colors ${
                        minError || maxError || amountError ? 'bg-error/5' : ''
                      }`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-3">
                          <Controller
                            name={`return_weight_ranges.${index}.min_weight`}
                            control={control}
                            render={({ field: formField }) => (
                              <div>
                                <Input
                                  {...formField}
                                  type="number"
                                  step="0.001"
                                  placeholder="0.000"
                                  className={`w-full h-8 text-sm ${minError ? 'border-error' : ''}`}
                                  min="0"
                                  disabled={isFirst}
                                  onChange={(e) => {
                                    const normalizedValue = e.target.value.replace(',', '.');
                                    const value = parseFloat(normalizedValue);
                                    if (!isNaN(value) && value >= 0) {
                                      const rounded = roundToThreeDecimals(value);
                                      formField.onChange(rounded);
                                    } else if (normalizedValue === '' || normalizedValue === '-') {
                                      formField.onChange(e.target.value);
                                    }
                                  }}
                                  value={isFirst ? 0 : formField.value}
                                />
                                {minError && <p className="text-xs text-error mt-0.5">{minError.message}</p>}
                              </div>
                            )}
                          />
                        </div>
                        <div className="col-span-3">
                          <Controller
                            name={`return_weight_ranges.${index}.max_weight`}
                            control={control}
                            render={({ field: formField }) => (
                              <div>
                                <Input
                                  {...formField}
                                  type="number"
                                  step="0.001"
                                  placeholder="0.000"
                                  className={`w-full h-8 text-sm ${maxError ? 'border-error' : ''}`}
                                  min="0"
                                  onChange={(e) => {
                                    const normalizedValue = e.target.value.replace(',', '.');
                                    const value = parseFloat(normalizedValue);
                                    if (!isNaN(value) && value >= 0) {
                                      const rounded = roundToThreeDecimals(value);
                                      formField.onChange(rounded);
                                    } else if (normalizedValue === '' || normalizedValue === '-') {
                                      formField.onChange(e.target.value);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const normalizedValue = e.target.value.replace(',', '.');
                                    const value = parseFloat(normalizedValue);
                                    if (!isNaN(value) && value >= 0) {
                                      const rounded = roundToThreeDecimals(value);
                                      const minValue = Number(returnWeightRanges?.[index]?.min_weight ?? 0);
                                      if (rounded <= minValue) {
                                        const validMax = roundToThreeDecimals(minValue + 0.001);
                                        formField.onChange(validMax);
                                        if (index < returnWeightRangesFields.length - 1) {
                                          const nextMinValue = roundToThreeDecimals(validMax + 0.001);
                                          setValue(`return_weight_ranges.${index + 1}.min_weight`, nextMinValue);
                                        }
                                      } else {
                                        if (index < returnWeightRangesFields.length - 1) {
                                          const nextMinValue = roundToThreeDecimals(rounded + 0.001);
                                          setValue(`return_weight_ranges.${index + 1}.min_weight`, nextMinValue);
                                        }
                                      }
                                    }
                                    formField.onBlur();
                                  }}
                                />
                                {maxError && <p className="text-xs text-error mt-0.5">{maxError.message}</p>}
                              </div>
                            )}
                          />
                        </div>
                        <div className="col-span-5">
                          <Controller
                            name={`return_weight_ranges.${index}.amount`}
                            control={control}
                            render={({ field: formField }) => (
                              <div>
                                <Input
                                  {...formField}
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  className={`w-full h-8 text-sm ${amountError ? 'border-error' : ''}`}
                                  min="0"
                                  onChange={(e) => {
                                    const normalizedValue = e.target.value.replace(',', '.');
                                    const value = parseFloat(normalizedValue);
                                    if (!isNaN(value) && value >= 0) {
                                      const rounded = roundToTwoDecimals(value);
                                      formField.onChange(rounded);
                                    } else if (normalizedValue === '' || normalizedValue === '-') {
                                      formField.onChange(e.target.value);
                                    }
                                  }}
                                />
                                {amountError && <p className="text-xs text-error mt-0.5">{amountError.message}</p>}
                              </div>
                            )}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {returnWeightRangesFields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const isRemovingLast = index === returnWeightRangesFields.length - 1;
                                removeReturnRange(index);
                                if (!isRemovingLast) {
                                  setTimeout(() => {
                                    if (index === 0) {
                                      setValue('return_weight_ranges.0.min_weight', 0);
                                      return;
                                    }

                                    const prevMax = getValues(`return_weight_ranges.${index - 1}.max_weight`);
                                    if (prevMax !== undefined) {
                                      const nextMinValue = roundToThreeDecimals(Number(prevMax) + 0.001);
                                      setValue(`return_weight_ranges.${index}.min_weight`, nextMinValue);
                                    }
                                  }, 0);
                                }
                              }}
                              className="h-8 w-8 p-0 text-error hover:text-error hover:bg-error/10"
                            >
                              <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border-t border-card-border pt-4">
        <p className="text-xs text-subtext mb-3">💡 {t('form.shipping.codHint')}</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('form.shipping.codFixedFee', { currency })}</Label>
            <Controller
              name="cod_fixed_fee"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  min="0"
                  onChange={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    // Permitir escribir decimales con ceros (ej: 1.05) sin redondear mientras se escribe
                    if (/^\d*\.?\d*$/.test(normalizedValue) || normalizedValue === '') {
                      field.onChange(normalizedValue);
                    }
                  }}
                  onBlur={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    const value = parseFloat(normalizedValue);
                    if (!isNaN(value) && value >= 0) {
                      field.onChange(String(roundToTwoDecimals(value)));
                    }
                    field.onBlur();
                  }}
                />
              )}
            />
          </div>
          <div>
            <Label>{t('form.shipping.codPercentageFee')}</Label>
            <Controller
              name="cod_percentage_fee"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="1.5"
                  min="0"
                  onChange={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    if (/^\d*\.?\d*$/.test(normalizedValue) || normalizedValue === '') {
                      field.onChange(normalizedValue);
                    }
                  }}
                  onBlur={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    const value = parseFloat(normalizedValue);
                    if (!isNaN(value) && value >= 0) {
                      field.onChange(String(roundToTwoDecimals(value)));
                    }
                    field.onBlur();
                  }}
                />
              )}
            />
            <p className="text-xs text-subtext mt-1">{t('form.shipping.percentageHint')}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-card-border pt-4">
        <p className="text-xs text-subtext mb-3">💡 {t('form.shipping.extraChargesHint')}</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('form.shipping.fuelSurcharge')}</Label>
            <Controller
              name="fuel_surcharge_percent"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  min="0"
                  onChange={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    if (/^\d*\.?\d*$/.test(normalizedValue) || normalizedValue === '') {
                      field.onChange(normalizedValue);
                    }
                  }}
                  onBlur={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    const value = parseFloat(normalizedValue);
                    if (!isNaN(value) && value >= 0) {
                      field.onChange(String(roundToTwoDecimals(value)));
                    }
                    field.onBlur();
                  }}
                />
              )}
            />
          </div>
          <div>
            <Label>{t('form.shipping.extraWeightCharge', { currency })}</Label>
            <Controller
              name="extra_weight_charge_amount_per_kg"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  min="0"
                  onChange={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    if (/^\d*\.?\d*$/.test(normalizedValue) || normalizedValue === '') {
                      field.onChange(normalizedValue);
                    }
                  }}
                  onBlur={(e) => {
                    const normalizedValue = e.target.value.replace(',', '.');
                    const value = parseFloat(normalizedValue);
                    if (!isNaN(value) && value >= 0) {
                      field.onChange(String(roundToTwoDecimals(value)));
                    }
                    field.onBlur();
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div>
        <Label>{t('form.notes')}</Label>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextArea {...field} value={field.value || ''} placeholder={t('form.notesPlaceholder')} rows={2} />
          )}
        />
      </div>
    </div>
  );
});

AddShippingItemFormComponent.displayName = 'AddShippingItemForm';

// Memoize para evitar re-renders innecesarios
export const AddShippingItemForm = React.memo(AddShippingItemFormComponent);
